/*
  testimonial-video-grid.js

  Behaviour for sections/testimonial-video-grid.liquid: a grid of Shopify-hosted portrait videos
  that shows only its first row (2 below 750px, 3 from 750px) until the visitor asks for more.
  Each video shows its poster and a play button, and plays inline in its card when clicked.

  Markup contract
  ---------------
  <testimonial-video-grid class="testimonial-video-grid__body">   the custom element wraps everything
    <ul class="testimonial-video-grid__list" id="TestimonialVideoGridList--...">
      <li class="testimonial-video-grid__item">
        <div class="testimonial-video-grid__media">
          <video controls playsinline preload="none" poster="...">   from video_tag
          <button data-tvg-play hidden aria-label="Play video">     shown by this script
    <div class="testimonial-video-grid__footer">                    only rendered with 3+ videos
      <button data-tvg-toggle aria-expanded="false" aria-controls="TestimonialVideoGridList--...">
        <span data-tvg-toggle-more>Show more</span>
        <span data-tvg-toggle-less hidden>Show less</span>

  - Which items are hidden is decided by CSS alone (nth-child, per breakpoint), keyed off the
    is-expanded class on the custom element. This script only flips that class, so the
    breakpoint lives in one place.
  - The markup ships with native controls and the play buttons hidden, so the videos still work
    if this script never runs. On connect it drops the controls and shows the play buttons;
    clicking one removes it, brings the controls back and plays the video with sound.
  - Only one video plays at a time per grid. Starting one pauses the others, and collapsing the
    grid pauses any video that has just been hidden.
  - In the theme editor, selecting a block that is hidden behind the toggle expands the grid so
    the merchant can see what they selected. A section reload replaces the element, and
    disconnectedCallback cleans up after the old one.
*/

if (!customElements.get('testimonial-video-grid')) {
  const selectors = {
    item: '.testimonial-video-grid__item',
    play: '[data-tvg-play]',
    toggle: '[data-tvg-toggle]',
    toggleMore: '[data-tvg-toggle-more]',
    toggleLess: '[data-tvg-toggle-less]',
  };

  const classes = {
    expanded: 'is-expanded',
  };

  class TestimonialVideoGrid extends HTMLElement {
    constructor() {
      super();

      this.onClick = this.onClick.bind(this);
      this.onPlay = this.onPlay.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.toggle = this.querySelector(selectors.toggle);
      this.toggleMore = this.querySelector(selectors.toggleMore);
      this.toggleLess = this.querySelector(selectors.toggleLess);
      this.videos = Array.from(this.querySelectorAll('video'));

      this.querySelectorAll(selectors.play).forEach((button) => {
        const video = button.parentElement.querySelector('video');
        if (!video) return;

        video.controls = false;
        button.hidden = false;
      });

      this.addEventListener('click', this.onClick);
      /* play does not bubble, so listen in the capture phase. */
      this.addEventListener('play', this.onPlay, true);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('play', this.onPlay, true);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onClick(event) {
      const play = event.target.closest(selectors.play);
      if (play && this.contains(play)) {
        this.playFrom(play);
        return;
      }

      const toggle = event.target.closest(selectors.toggle);
      if (toggle && toggle === this.toggle) {
        this.setExpanded(!this.classList.contains(classes.expanded));
      }
    }

    playFrom(button) {
      const video = button.parentElement.querySelector('video');
      if (!video) return;

      button.remove();
      video.controls = true;

      const attempt = video.play();
      /* A rejected play (e.g. blocked by the browser) leaves the native controls to fall back on. */
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    }

    onPlay(event) {
      const current = event.target;
      if (!(current instanceof HTMLVideoElement)) return;

      this.videos.forEach((video) => {
        if (video !== current && !video.paused) video.pause();
      });
    }

    setExpanded(expanded) {
      this.classList.toggle(classes.expanded, expanded);

      if (this.toggle) this.toggle.setAttribute('aria-expanded', String(expanded));
      if (this.toggleMore) this.toggleMore.hidden = expanded;
      if (this.toggleLess) this.toggleLess.hidden = !expanded;

      if (expanded) return;

      /* Hidden items have no layout box, so offsetParent tells us which videos just disappeared. */
      this.videos.forEach((video) => {
        if (!video.paused && video.offsetParent === null) video.pause();
      });

      /* Collapsing can pull the page up by several screens; keep the toggle where the visitor is. */
      if (this.toggle && this.toggle.getBoundingClientRect().top < 0) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.toggle.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
    }

    onBlockSelect(event) {
      const item = event.target.closest ? event.target.closest(selectors.item) : null;
      if (!item || !this.contains(item)) return;

      if (item.offsetParent === null) this.setExpanded(true);
    }
  }

  customElements.define('testimonial-video-grid', TestimonialVideoGrid);
}
